import { useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import { LIST_ACTIONS, makeListReducer } from 'lib/list-helper/makeListReducer';
import useQueryList from 'lib/list-helper/useQueryList';
import useQueryFilters from 'lib/useQueryFilters';
import useConfirm from 'lib/components/useConfirm/useConfirm';
import dynamicModificationRulesService from 'lib/dynamicModificationRulesService';
import settingsService from './settingsService';

const { reducer, initialState } = makeListReducer();

const ALL_RULES = { value: '', label: 'All rules' };

const fetchStages = debounce((params, dispatch) => {
  dispatch({ type: LIST_ACTIONS.SET_LOADING, payload: true });

  return settingsService
    .getDynamicModificationStages(params)
    .then((results) => {
      dispatch({ type: LIST_ACTIONS.APP_LOADS_DATA, payload: { ...results } });
    })
    .catch(() => {
      dispatch({
        type: LIST_ACTIONS.SET_ERRORS,
        payload: { errors: ['The stages could not be loaded. Please refresh the page.'] },
      });
    })
    .then(() => dispatch({ type: LIST_ACTIONS.SET_LOADING, payload: false }));
}, 400);

const useSettingsDynamicStages = ({ location, history }) => {
  const { state, dispatch, setSearch, setPage, setPageSize } = useQueryList(
    location,
    history,
    reducer,
    initialState,
  );
  const [ruleOptions, setRuleOptions] = useState([ALL_RULES]);
  const filters = useQueryFilters(location, history, { ruleId: ruleOptions });
  const { confirm } = useConfirm();

  const listParams = {
    search: state.search,
    page: state.page,
    pageSize: state.pageSize,
    ruleId: filters.values.ruleId || null,
  };

  useEffect(() => {
    dynamicModificationRulesService
      .getRuleOptions()
      .then((options) =>
        setRuleOptions([
          ALL_RULES,
          ...options.map((option) => ({ ...option, value: `${option.value}` })),
        ]),
      );
  }, []);

  useEffect(() => {
    fetchStages(listParams, dispatch);
  }, [state.search, state.page, state.pageSize, filters.values.ruleId]);

  const userResetsStage = (level) =>
    confirm({
      title: 'Reset to the starting stage?',
      body: `The question will be asked again for ${
        level.assetExternalId || 'this product'
      } at ${level.locationName || 'this factory'}, and its counts start from zero.`,
      confirmText: 'Reset',
    }).then((isConfirmed) => {
      if (!isConfirmed) return;
      settingsService
        .resetDynamicModificationStage(level.id)
        .then(() => fetchStages(listParams, dispatch));
    });

  return {
    state,
    setSearch,
    setPage,
    setPageSize,
    ruleFilterProps: filters.getProps('ruleId'),
    userResetsStage,
  };
};

export default useSettingsDynamicStages;
